import * as Stardog from 'stardog';
import { getStardogConnection } from 'vet-bones/utils';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { projectModelSelector } from 'src/selectors/projectModel';
import { ProjectModel } from 'src/store/state/projects';
import { AppDispatch } from 'src/store/store';
import { ReduxStateGetter } from 'src/store/types';
import {
  AllSupportedModelPartsBindingResult,
  allSupportedModelPartsQuery,
} from 'src/templates/queries/allSupportedModelParts';
import { GEO_DATA_KEY_BY_IRI } from 'src/templates/queries/geoData';
import {
  CLASS_IRIS,
  DESCRIPTION_IRIS,
  DESIGNER_COLOR_ID_IRI,
  DOMAIN_IRIS,
  LABEL_IRIS,
  MAP_IRIS,
  OWL_DATATYPE_PROPERTY_IRI,
  OWL_OBJECT_PROPERTY_IRI,
  RANGE_IRIS,
  RDFS_SUB_CLASS_OF_IRI,
  RDFS_SUB_PROPERTY_OF_IRI,
  RDF_TYPE_IRI,
  RULE_CONTENT_IRI,
  RULE_SPARQL_RULE_IRI,
  SCHEMA_ORG_DATE_PUBLISHED_IRI,
  SCHEMA_ORG_HTTP_SUPERSEDED_BY_IRI,
  SCHEMA_ORG_SUPERSEDED_BY_IRI,
  SYSTEM_VOICEBOX_INSTRUCTIONS_IRI,
} from 'src/templates/queries/iris';
import { hasNonModelPartsQuery } from 'src/templates/queries/nonModelParts';
import { unsupportedPredicatesQuery } from 'src/templates/queries/unsupportedPredicates';

export const isBindingValid = (
  model: ProjectModel,
  binding: AllSupportedModelPartsBindingResult,
  geoPropertyIris: string[]
) => {
  const predicateIri = binding.predicate.value;

  if (
    SCHEMA_ORG_SUPERSEDED_BY_IRI === predicateIri ||
    SCHEMA_ORG_HTTP_SUPERSEDED_BY_IRI === predicateIri
  ) {
    const isSubjectUsedAsClass = Boolean(
      model.classes?.[binding.subject.value]
    );
    const isSubjectUsedAsProperty = Boolean(
      model.properties?.[binding.subject.value]
    );

    const childIri = binding.subject.value;
    const parentIri = binding.object.value;
    if (isSubjectUsedAsClass) {
      const isSubclassUsed =
        model.classes?.[childIri]?.parents?.includes(parentIri);
      const isValidType = binding.object.type === 'uri';
      if (isSubclassUsed && isValidType) {
        return true;
      }
    }
    if (isSubjectUsedAsProperty) {
      const isSubPropertyUsed =
        model.properties?.[childIri]?.parents?.includes(parentIri);
      const isValidType = binding.object.type === 'uri';
      if (isSubPropertyUsed && isValidType) {
        return true;
      }
    }
  }

  if (DESCRIPTION_IRIS.includes(predicateIri)) {
    const isDescriptionUsed =
      binding.object.value === model.descriptionsByIri?.[binding.subject.value];
    if (isDescriptionUsed) {
      return true;
    }
  }

  if (LABEL_IRIS.includes(predicateIri)) {
    const isLabelUsed =
      binding.object.value === model.labelsByIri?.[binding.subject.value];
    const isLabelValidType = binding.object.type === 'literal';

    if (isLabelUsed && isLabelValidType) {
      return true;
    }
  }

  if (MAP_IRIS.includes(predicateIri)) {
    const classIri = binding.subject.value;
    const geoKey = GEO_DATA_KEY_BY_IRI[predicateIri];
    const isUsed =
      model.geoDataByIri?.[classIri]?.[geoKey]?.value === binding.object.value;
    const isValidType =
      binding.object.type === 'literal' || binding.object.type === 'uri';
    if (isUsed && isValidType) {
      return true;
    }
  }

  if (DOMAIN_IRIS.includes(predicateIri)) {
    const propertyIri = binding.subject.value;
    const isDomainUsed = model.properties?.[propertyIri]?.domain?.includes(
      binding.object.value
    );
    const isValidType = binding.object.type === 'uri';
    if (isDomainUsed && isValidType) {
      return true;
    }
  }

  if (RANGE_IRIS.includes(predicateIri)) {
    const propertyIri = binding.subject.value;
    const isRangeUsed = model.properties?.[propertyIri]?.range?.includes(
      binding.object.value
    );
    const isValidType = binding.object.type === 'uri';
    if (isRangeUsed && isValidType) {
      return true;
    }
  }

  if (RDFS_SUB_CLASS_OF_IRI === predicateIri) {
    const childIri = binding.subject.value;
    const parentIri = binding.object.value;
    const isSubclassUsed =
      model.classes?.[childIri]?.parents?.includes(parentIri);
    const isValidType = binding.object.type === 'uri';
    if (isSubclassUsed && isValidType) {
      return true;
    }
  }

  if (RDFS_SUB_PROPERTY_OF_IRI === predicateIri) {
    const childIri = binding.subject.value;
    const parentIri = binding.object.value;
    const isSubPropertyUsed =
      model.properties?.[childIri]?.parents?.includes(parentIri);
    const isValidType = binding.object.type === 'uri';
    if (isSubPropertyUsed && isValidType) {
      return true;
    }
  }

  if (RDF_TYPE_IRI === predicateIri) {
    const classIri = binding.object.value;

    if (CLASS_IRIS.includes(classIri)) {
      const isSubjectUsedAsClass = Boolean(
        model.classes?.[binding.subject.value]
      );
      return isSubjectUsedAsClass;
    }

    if (classIri === RULE_SPARQL_RULE_IRI) {
      const isSubjectUsedAsRule = model.rules?.[binding.subject.value];
      return isSubjectUsedAsRule;
    }

    if (classIri === OWL_DATATYPE_PROPERTY_IRI) {
      const property = model.properties?.[binding.subject.value];
      const isSubjectUsedAsDatatypeProperty = property && property.isDatatype;
      return isSubjectUsedAsDatatypeProperty;
    }

    if (classIri === OWL_OBJECT_PROPERTY_IRI) {
      const property = model.properties?.[binding.subject.value];
      const isSubjectUsedAsObjectProperty = property && !property.isDatatype;
      return isSubjectUsedAsObjectProperty;
    }

    return false;
  }

  if (RULE_CONTENT_IRI === predicateIri) {
    const ruleExists = model.rules?.[binding.subject.value];
    return ruleExists;
  }

  if (geoPropertyIris.includes(predicateIri)) {
    const isThereAMatch = Object.entries(model.geoDataByIri || {}).some(
      ([classIri, geoData]) => {
        return Object.values(geoData).some((geoProperty) => {
          return (
            geoProperty.isAttribute &&
            geoProperty.value === predicateIri &&
            binding.subject.value === classIri
          );
        });
      }
    );
    return isThereAMatch;
  }

  if (DESIGNER_COLOR_ID_IRI === predicateIri) {
    return true;
  }

  if (SCHEMA_ORG_DATE_PUBLISHED_IRI === predicateIri) {
    return true;
  }

  if (SYSTEM_VOICEBOX_INSTRUCTIONS_IRI === predicateIri) {
    return true;
  }

  return false;
};

export const getInvalidBindings = (
  model: ProjectModel,
  bindings: AllSupportedModelPartsBindingResult[],
  geoDataIris: string[]
) => {
  const unusedBindings = bindings.filter(
    (binding) => !isBindingValid(model, binding, geoDataIris)
  );

  return unusedBindings;
};

export const checkUnsupportedModelParts =
  ({
    projectId,
    projectModelId,
    databaseId,
    namedGraphs,
  }: {
    projectId: string;
    projectModelId: string;
    databaseId: string;
    namedGraphs: string[];
  }) =>
  async (dispatch: AppDispatch, getState: ReduxStateGetter) => {
    const state = getState();
    const model = projectModelSelector(projectId, projectModelId)(state);
    const { connection } = state.connections;
    if (!connection) {
      return;
    }

    if (!model) {
      return;
    }

    const stardogConnection = getStardogConnection(connection, {
      agent: 'Designer',
    });

    const geoDataIris = Array.from(
      Object.values(model.geoDataByIri || {}).reduce<Set<string>>(
        (acc, geoData) => {
          Object.values(geoData).forEach((geoDataProperty) => {
            if (geoDataProperty.isAttribute) {
              acc.add(geoDataProperty.value);
            }
          });
          return acc;
        },
        new Set()
      )
    );

    const unsupportedPredicatesResponse = await Stardog.query.execute(
      stardogConnection,
      databaseId,
      unsupportedPredicatesQuery(geoDataIris),
      undefined,
      { 'default-graph-uri': namedGraphs }
    );

    if (!unsupportedPredicatesResponse.ok) {
      return;
    }

    if (unsupportedPredicatesResponse.body === true) {
      dispatch(
        projectsActionCreators.showedUnsupportedModelPartsWarning({
          id: projectId,
          modelId: projectModelId,
        })
      );
      return;
    }

    const nonModelPartsResponse = await Stardog.query.execute(
      stardogConnection,
      databaseId,
      hasNonModelPartsQuery(geoDataIris),
      undefined,
      { 'default-graph-uri': namedGraphs }
    );

    if (!nonModelPartsResponse.ok) {
      return;
    }

    if (nonModelPartsResponse.body === true) {
      dispatch(
        projectsActionCreators.showedUnsupportedModelPartsWarning({
          id: projectId,
          modelId: projectModelId,
        })
      );
      return;
    }

    const supportedModelPartsResponse = await Stardog.query.execute(
      stardogConnection,
      databaseId,
      allSupportedModelPartsQuery(geoDataIris),
      undefined,
      { 'default-graph-uri': namedGraphs }
    );

    if (!supportedModelPartsResponse.ok) {
      return;
    }

    const modelPartsResults = (supportedModelPartsResponse.body?.results
      ?.bindings || []) as AllSupportedModelPartsBindingResult[];
    const unusedBindings = getInvalidBindings(
      model,
      modelPartsResults,
      geoDataIris
    );

    if (unusedBindings.length) {
      dispatch(
        projectsActionCreators.showedUnsupportedModelPartsWarning({
          id: projectId,
          modelId: projectModelId,
        })
      );
    }
  };
